import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import ownedTicket from "../graphql/queries/ownedTicket";
import styled from "styled-components";
import GlobalStyles from "../components/_globalStyles/globalStyles";
import TicketHeader from "../components/organisms/TicketHeader";
import Ticket from "../components/templates/Ticket";
import { RouteComponentProps, Redirect } from "react-router-dom";
import { heightPercentsToPx } from "../util/helpers";
import {
  OwnedTicketData,
  OwnedTicketDataVariables
} from "../interfaces/graphql/OwnedTicketData";
import {
  ValidateTicketData,
  ValidateTicketDataVariables
} from "../interfaces/graphql/ValidateTicketData";
import { TicketDetailsProps } from "../interfaces/routerProps";
import validateTicket from "../graphql/mutations/validateTicket";
import Loader from "../components/atoms/Loader";
import { setCookie } from "../util/cookieProvider";
import ConfirmationPopup from "../components/organisms/ConfirmationPopup";
import { useTranslation } from "react-i18next";

const TicketDetailsView = styled.main`
  min-height: ${heightPercentsToPx(100)};
`;

type Props = RouteComponentProps<TicketDetailsProps>;

const TicketDetails = (props: Props): React.ReactElement | null => {
  const { t } = useTranslation();
  const ticketId = props.match.params.ticketId;

  const [validateError, setValidateError] = useState<string | null>();

  const { data, loading, error, refetch } = useQuery<
    OwnedTicketData,
    OwnedTicketDataVariables
  >(ownedTicket, {
    variables: {
      id: Number(ticketId)
    },
    fetchPolicy: "network-only"
  });

  const [validate] = useMutation<
    ValidateTicketData,
    ValidateTicketDataVariables
  >(validateTicket);

  if (error) return <Redirect to="/" />;

  if (loading) {
    return <Loader />;
  }

  if (data?.ownedTicket.__typename === "NotFoundError")
    return <Redirect to="/" />;

  if (data?.ownedTicket.__typename === "NotAuthenticatedError") {
    setCookie("socialTicketLink", ticketId, { path: "/" });
    return <Redirect to="/login" />;
  }

  const myMunicipality =
    data?.me.__typename === "User" &&
    (data.me.profile.__typename === "Child" ||
      data.me.profile.__typename === "Parent") &&
    data.me.profile.municipality.name;

  const ticket =
    (data?.ownedTicket.__typename === "GrantedTicket" ||
      data?.ownedTicket.__typename === "BookedTicket") &&
    data.ownedTicket;

  const validTo =
    ticket &&
    ticket.ticketCollection.ticket.validTo &&
    new Date(ticket.ticketCollection.ticket.validTo);

  const validFrom =
    ticket &&
    ticket.ticketCollection.ticket.validFrom &&
    new Date(ticket.ticketCollection.ticket.validFrom);

  const ticketDetails: any = ticket
    ? {
        serviceProvider: ticket.ticketCollection.serviceProvider.name,
        title: ticket.ticketCollection.ticket.title,
        canBeUsedBy: ticket.ticketCollection.ticket.canBeUsedBy,
        validTo: validTo,
        validFrom: validFrom,
        description: ticket.ticketCollection.ticket.description,
        linkUrl: ticket.ticketCollection.ticket.linkUrl,
        entryLimit: ticket.ticketCollection.ticket.entryLimit,
        timesUsed: ticket.timesUsed,
        code: ticket.code,
        minimumAge:
          ticket.ticketCollection.ticket.minAge === 0
            ? null
            : ticket.ticketCollection.ticket.minAge,
        discount:
          ticket.__typename === "BookedTicket" &&
          ticket.ticketCollection.ticket.discount
            ? ticket.ticketCollection.ticket.discount
            : null,
        type: data?.ownedTicket.__typename,
        overlayColor: ticket.ticketCollection.style.overlayColor,
        images: Object.values(ticket.ticketCollection.style.uploads).length
          ? Object.values(ticket.ticketCollection.style.uploads)
          : null
      }
    : undefined;

  const handleValidateTicket = () => {
    validate({
      variables: {
        input: {
          grantedTicketId: Number(ticketId)
        }
      }
    }).then((res) => {
      if (
        res.data?.validateTicket.__typename === "InvalidTicketValidationError"
      ) {
        setValidateError(res.data?.validateTicket.message);
      }
      refetch();
    });
  };

  const childId = props.location.search.split("=")[1];

  return (
    <TicketDetailsView>
      <GlobalStyles />
      <TicketHeader
        backRoute={childId ? `/?child=${childId}` : "/"}
        idPrefix="ticket-header"
      />
      <Ticket
        myMunicipality={myMunicipality}
        ticketDetails={ticketDetails}
        idPrefix="ticket"
        handleValidateTicket={handleValidateTicket}
      />
      {validateError && (
        <ConfirmationPopup
          idPrefix="validate-error"
          open={!!validateError}
          title={t("general.oops")}
          subtitle={validateError}
          cancelButtonName={t("general.close")}
          onCancel={() => setValidateError(null)}
        />
      )}
    </TicketDetailsView>
  );
};

export default TicketDetails;
